import React, { useEffect } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dna, Microscope, TestTube, FlaskConical, ShieldCheck, Activity, ArrowLeft, Star, Award, Users, Clock, Phone, Heart, Droplets, Stethoscope, Syringe, Sparkles } from "lucide-react";
import Reveal from "@/components/Reveal";
import AnimatedCounter from "@/components/AnimatedCounter";
import CanonicalTag from "@/components/CanonicalTag";
import MetaTags from "@/components/MetaTags";
const About: React.FC = () => {
    const description = "آشنایی با آزمایشگاه تشخیص پزشکی سلامت: تیم متخصص، فناوری‌های پیشرفته و رویکرد انسان‌محور برای دقیق‌ترین نتایج تشخیصی.";
    const specialties = [
        { icon: Microscope, label: "بیوشیمی", color: "from-blue-500 to-blue-600" },
        { icon: TestTube, label: "هورمون‌شناسی", color: "from-green-500 to-green-600" },
        { icon: ShieldCheck, label: "ایمونولوژی", color: "from-purple-500 to-purple-600" },
        { icon: Activity, label: "هماتولوژی", color: "from-red-500 to-red-600" },
        { icon: Dna, label: "تشخیص مولکولی (PCR)", color: "from-indigo-500 to-indigo-600" },
        { icon: FlaskConical, label: "سیتولوژی", color: "from-orange-500 to-orange-600" },
    ];
    const stats = [
        { icon: Users, value: 15000, label: "مراجع راضی", suffix: "+", color: "text-blue-600" },
        { icon: Award, value: 98, label: "رضایت", suffix: "%", color: "text-green-600" },
        { icon: Clock, value: 24, label: "ساعت پشتیبانی", suffix: "/7", color: "text-purple-600" },
        { icon: Star, value: 15, label: "سال تجربه", suffix: "+", color: "text-orange-600" },
    ];
    return (<div className="min-h-screen bg-gradient-to-b from-slate-50 via-blue-50/30 to-slate-50 text-foreground">
      <CanonicalTag path="/about"/>
      <MetaTags title="درباره ما | آزمایشگاه تشخیص پزشکی سلامت" description={description} keywords="درباره ما, آزمایشگاه سلامت, تیم متخصص, فناوری پیشرفته, تشخیص پزشکی, شهرقدس, تهران" ogImage="https://www.salamatlab.com/about-preview.jpg" path="/about"/>
      <Header />

      <main id="main" className="pt-36">
        
        <div className="container mx-auto px-4 pb-4">
          <a href="/" aria-label="بازگشت به صفحه اصلی" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground underline-offset-4 hover:underline">
            <ArrowLeft className="w-4 h-4 rtl:rotate-180"/>
            بازگشت به صفحه اصلی
          </a>
        </div>
        
        <section id="hero" className="relative min-h-[80vh] md:min-h-[90vh] flex items-center justify-center overflow-hidden">
          
          <div aria-hidden className="absolute inset-0 -z-10">
            <div className="absolute -top-20 -left-20 h-96 w-96 rounded-full bg-primary/15 blur-3xl animate-pulse"/>
            <div className="absolute -bottom-24 -right-16 h-[28rem] w-[28rem] rounded-full bg-blue-400/20 blur-3xl animate-pulse" style={{ animationDelay: '2s' }}/>
            <div className="absolute top-1/3 right-1/4 h-64 w-64 rounded-full bg-indigo-400/10 blur-2xl animate-float"/>
            <div className="absolute bottom-1/3 left-1/4 h-48 w-48 rounded-full bg-teal-400/15 blur-2xl animate-float" style={{ animationDelay: '1s' }}/>
          </div>
          
          
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {[...Array(8)].map((_, i) => (<div key={i} className="absolute w-2 h-2 bg-primary/40 rounded-full animate-float" style={{
                left: `${15 + i * 12}%`,
                top: `${25 + (i % 3) * 25}%`,
                animationDelay: `${i * 0.4}s`,
                animationDuration: `${3 + i}s`
            }}/>))}
            <Microscope className="absolute top-[15%] left-[10%] w-8 h-8 text-primary/20 animate-float" style={{ animationDelay: '0.5s' }}/>
            <Dna className="absolute top-[20%] right-[15%] w-10 h-10 text-blue-400/20 animate-float" style={{ animationDelay: '1.2s' }}/>
            <Heart className="absolute bottom-[25%] left-[20%] w-6 h-6 text-rose-400/25 animate-float" style={{ animationDelay: '0.8s' }}/>
            <TestTube className="absolute bottom-[30%] right-[10%] w-7 h-7 text-teal-400/20 animate-float" style={{ animationDelay: '1.5s' }}/>
            <Stethoscope className="absolute top-[40%] right-[8%] w-6 h-6 text-indigo-400/15 animate-float" style={{ animationDelay: '2s' }}/>
            <Droplets className="absolute top-[60%] left-[8%] w-5 h-5 text-sky-400/20 animate-float" style={{ animationDelay: '0.3s' }}/>
          </div>

          <div className="container mx-auto px-4 text-center space-y-6 relative z-10">
            <Reveal>
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-primary/15 to-blue-500/10 text-primary px-6 py-3 rounded-full text-sm font-semibold mb-6 animate-fade-in border border-primary/20 shadow-sm">
                <Star className="w-4 h-4"/>
                <span>آزمایشگاه مورد اعتماد شهرقدس</span>
              </div>
            </Reveal>
            
            <Reveal delay={200}>
              <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight">
                <span className="bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                  سلامت، در نهایتِ دقت
                </span>
              </h1>
            </Reveal>
            
            <Reveal delay={400}>
              <p className="mx-auto max-w-2xl text-muted-foreground text-lg md:text-xl">
                آینده تشخیص، وقف سلامتی شما.
              </p>
            </Reveal>
            
            <Reveal delay={600}>
              <div className="flex justify-center">
                <a href="#team" className="inline-flex" aria-label="مشاهده تیم متخصص">
                  <Button size="lg" className="px-8 py-4 text-lg font-bold bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 text-white border-2 border-primary">
                    با متخصصین ما آشنا شوید
                    <ArrowLeft className="mr-2 rtl:ml-2 rtl:mr-0"/>
                  </Button>
                </a>
              </div>
            </Reveal>
            
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-primary/60 animate-bounce-slow">
              <span className="text-xs font-medium">مشاهده بیشتر</span>
              <div className="flex gap-1">
                <div className="w-1.5 h-1.5 rounded-full bg-primary/50"/>
                <div className="w-1.5 h-1.5 rounded-full bg-primary/40"/>
                <div className="w-1.5 h-1.5 rounded-full bg-primary/30"/>
              </div>
            </div>
          </div>
        </section>

        
        <section className="container mx-auto px-4 py-20">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
            { ...stats[0], bg: "from-blue-500/10 to-blue-600/5", border: "border-blue-200/60" },
            { ...stats[1], bg: "from-emerald-500/10 to-emerald-600/5", border: "border-emerald-200/60" },
            { ...stats[2], bg: "from-violet-500/10 to-violet-600/5", border: "border-violet-200/60" },
            { ...stats[3], bg: "from-amber-500/10 to-amber-600/5", border: "border-amber-200/60" },
        ].map((stat, index) => (<Reveal key={index} delay={index * 100}>
                <div className={`text-center p-8 rounded-2xl bg-gradient-to-br ${stat.bg} hover:scale-105 transition-all duration-300 border ${stat.border} shadow-sm hover:shadow-lg`}>
                  <div className={`mx-auto w-14 h-14 rounded-2xl bg-gradient-to-br ${stat.color.replace('text-', 'from-')} to-white/80 flex items-center justify-center mb-4 shadow-md`}>
                    <stat.icon className="w-7 h-7 text-white"/>
                  </div>
                  <div className={`text-3xl md:text-4xl font-bold ${stat.color} mb-2`}>
                    <AnimatedCounter end={stat.value} suffix={stat.suffix}/>
                  </div>
                  <div className="text-sm font-medium text-muted-foreground">{stat.label}</div>
                </div>
              </Reveal>))}
          </div>
          
          <div className="flex justify-center gap-2 mt-16">
            <div className="h-1 w-12 rounded-full bg-gradient-to-r from-transparent to-primary/60"/>
            <Sparkles className="w-5 h-5 text-primary/70"/>
            <div className="h-1 w-12 rounded-full bg-gradient-to-l from-transparent to-primary/60"/>
          </div>
        </section>

        
        <section className="relative py-24 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 via-transparent to-teal-500/5 -z-10"/>
          <div className="absolute top-20 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl -z-10"/>
          <div className="absolute bottom-20 left-0 w-48 h-48 bg-teal-500/10 rounded-full blur-2xl -z-10"/>
          <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <Reveal className="order-2 md:order-1 space-y-4">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-100 to-teal-100 text-emerald-800 px-5 py-2.5 rounded-full text-sm font-semibold mb-4 border border-emerald-200/50 shadow-sm">
                <Award className="w-4 h-4"/>
                <span>میراث تخصصی</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-extrabold">
                میراثی از تخصص و نوآوری
              </h2>
              <p className="text-muted-foreground leading-8">
                آزمایشگاه سلامت به‌دست اساتید دانشگاه و تیمی مجرب بنیان گذاشته شده و با بهره‌گیری از جدیدترین فناوری‌ها، متعهد به ارائه دقیق‌ترین و سریع‌ترین نتایج برای آرامش خاطر شماست.
              </p>
            </Reveal>
            <Reveal delay={200} className="order-1 md:order-2">
              <div className="relative group">
                <div className="absolute -inset-4 bg-gradient-to-r from-emerald-400/20 to-teal-400/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-500"></div>
                <div className="relative w-full h-[420px] rounded-2xl shadow-xl overflow-hidden bg-gradient-to-br from-emerald-500/20 via-primary/15 to-teal-500/20 flex items-center justify-center border border-emerald-200/30">
                  <Microscope className="w-36 h-36 text-primary/50 absolute"/>
                  <div className="absolute top-8 left-8">
                    <TestTube className="w-12 h-12 text-teal-400/30 animate-float"/>
                  </div>
                  <div className="absolute bottom-12 right-12">
                    <Dna className="w-10 h-10 text-emerald-400/25 animate-float" style={{ animationDelay: '1s' }}/>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/5 to-transparent"/>
                </div>
              </div>
            </Reveal>
          </div>
          </div>
        </section>

        
        <section id="team" className="relative py-24 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-blue-500/5 via-indigo-500/5 to-transparent -z-10"/>
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-32 bg-indigo-400/10 rounded-full blur-3xl -z-10"/>
          <div className="container mx-auto px-4">
          <Reveal>
            <header className="mb-14 text-center space-y-3">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 px-5 py-2.5 rounded-full text-sm font-semibold mb-4 border border-blue-200/50 shadow-sm">
                <Users className="w-4 h-4"/>
                <span>تیم متخصص</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-extrabold">تیم پیشروی ما</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                جمعی از متخصصین برجسته با رویکردی انسان‌محور و مبتنی بر شواهد علمی.
              </p>
            </header>
          </Reveal>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {[{
                name: "دکتر محمد نیاکان",
                role: "موسس آزمایشگاه، دکتری تخصصی میکروبیولوژی",
                org: "عضو هیئت علمی دانشگاه شاهد",
                delay: 200,
                color: "from-blue-500 to-indigo-600"
            }, {
                name: "دکتر فاطمه شکوهی",
                role: "دکترای علوم آزمایشگاهی",
                org: "مسئول فنی آزمایشگاه",
                delay: 400,
                color: "from-emerald-500 to-teal-600"
            }].map((m, i) => (<Reveal key={i} delay={m.delay}>
                <Card className="group bg-white/90 backdrop-blur-sm border-2 border-blue-200/50 hover:border-blue-300/70 hover:shadow-2xl hover:shadow-blue-500/10 transition-all duration-500 hover:-translate-y-2 overflow-hidden shadow-lg">
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-4">
                      <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${m.color} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                        <Users className="w-8 h-8 text-white"/>
                      </div>
                      <div>
                        <CardTitle className="text-xl font-bold">{m.name}</CardTitle>
                        <p className="text-sm text-primary font-medium mt-1">{m.role}</p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <p className="text-muted-foreground flex items-center gap-2">
                      <Award className="w-4 h-4 text-primary/70"/>
                      {m.org}
                    </p>
                  </CardContent>
                </Card>
              </Reveal>))}
          </div>
          </div>
        </section>

        
        <section className="relative py-24 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-violet-500/5 via-transparent to-fuchsia-500/5 -z-10"/>
          <div className="container mx-auto px-4">
          <Reveal>
            <header className="mb-12 text-center space-y-3">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-violet-100 to-purple-100 text-violet-800 px-5 py-2.5 rounded-full text-sm font-semibold mb-4 border border-violet-200/50 shadow-sm">
                <FlaskConical className="w-4 h-4"/>
                <span>فناوری پیشرفته</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-extrabold">پوشش جامع تشخیصی با فناوری پیشرفته</h2>
            </header>
          </Reveal>

          
          <Reveal delay={200}>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-5 mb-14">
              {specialties.map((s, idx) => (<div key={idx} className="group flex flex-col items-center gap-3 p-5 rounded-2xl bg-white/70 backdrop-blur-sm hover:bg-white transition-all duration-300 hover:scale-105 cursor-pointer border border-violet-200/40 hover:border-violet-300 hover:shadow-lg shadow-sm">
                  <div className={`p-4 rounded-2xl bg-gradient-to-br ${s.color} group-hover:scale-110 transition-transform duration-300 shadow-md`}>
                    <s.icon className="size-7 text-white"/>
                  </div>
                  <span className="text-sm text-foreground font-semibold text-center">{s.label}</span>
                </div>))}
            </div>
          </Reveal>

          
          <div className="grid md:grid-cols-2 gap-6">
            <Reveal delay={400}>
              <Card className="group bg-white/80 backdrop-blur-sm border-2 border-blue-200/50 shadow-md hover:shadow-xl transition-all duration-500 hover:-translate-y-1 overflow-hidden hover:border-blue-300/60">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/8 to-purple-500/8 opacity-0 group-hover:opacity-100 transition-opacity duration-500"/>
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-blue-100">
                      <Activity className="w-5 h-5 text-blue-600"/>
                    </div>
                    <span>پنل کامل بیماری‌های عفونی</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-muted-foreground leading-7 relative z-10">
                  ارزیابی سریع و دقیق طیف وسیعی از عوامل عفونی با حساسیت بالا، برای تشخیص زودهنگام و درمان مؤثر.
                </CardContent>
              </Card>
            </Reveal>
            
            <Reveal delay={600}>
              <Card className="group bg-white/80 backdrop-blur-sm border-2 border-emerald-200/50 shadow-md hover:shadow-xl transition-all duration-500 hover:-translate-y-1 overflow-hidden hover:border-emerald-300/60">
                <div className="absolute inset-0 bg-gradient-to-r from-green-500/8 to-blue-500/8 opacity-0 group-hover:opacity-100 transition-opacity duration-500"/>
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-emerald-100">
                      <FlaskConical className="w-5 h-5 text-emerald-600"/>
                    </div>
                    <span>الکتروفورز تخصصی (SDS-PAGE)</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-muted-foreground leading-7 relative z-10">
                  تحلیل پیشرفته پروتئین‌ها با وضوح بالا به‌منظور پایش‌های دقیق تحقیقاتی و بالینی.
                </CardContent>
              </Card>
            </Reveal>
          </div>
          <div className="flex justify-center mt-16 gap-4">
            <Syringe className="w-6 h-6 text-violet-400/50"/>
            <FlaskConical className="w-8 h-8 text-primary/60"/>
            <Microscope className="w-6 h-6 text-fuchsia-400/50"/>
          </div>
          </div>
        </section>

        
        <section className="relative py-24 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 via-orange-500/5 to-rose-500/5 -z-10"/>
          <div className="absolute top-1/2 right-0 w-80 h-80 bg-amber-400/10 rounded-full blur-3xl -z-10"/>
          <div className="absolute bottom-0 left-1/4 w-64 h-64 bg-rose-400/10 rounded-full blur-3xl -z-10"/>
          <div className="container mx-auto px-4">
            <Reveal>
            <header className="mb-16 text-center space-y-3">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-100 to-orange-100 text-amber-800 px-5 py-2.5 rounded-full text-sm font-semibold mb-4 border border-amber-200/50 shadow-sm">
                <Star className="w-4 h-4"/>
                <span>مزایای رقابتی</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-extrabold">چرا آزمایشگاه سلامت را انتخاب کنید؟</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                ما متعهد به ارائه بهترین خدمات با بالاترین کیفیت و دقت هستیم
              </p>
            </header>
          </Reveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
            {
                icon: ShieldCheck,
                title: "دقت بالا",
                description: "نتایج دقیق با استفاده از پیشرفته‌ترین تجهیزات",
                color: "from-green-500 to-green-600",
                delay: 100
            },
            {
                icon: Clock,
                title: "سرعت بالا",
                description: "ارائه نتایج در کوتاه‌ترین زمان ممکن",
                color: "from-blue-500 to-blue-600",
                delay: 200
            },
            {
                icon: Users,
                title: "تیم متخصص",
                description: "کارشناسان مجرب و با تجربه در حوزه تشخیص",
                color: "from-purple-500 to-purple-600",
                delay: 300
            },
            {
                icon: Award,
                title: "کیفیت تضمینی",
                description: "تضمین کیفیت تمامی خدمات و نتایج",
                color: "from-orange-500 to-orange-600",
                delay: 400
            }
        ].map((item, index) => (<Reveal key={index} delay={item.delay}>
                <div className="group text-center p-8 rounded-2xl bg-white/80 backdrop-blur-sm hover:bg-white transition-all duration-500 hover:scale-105 border-2 border-amber-200/50 hover:border-amber-300/70 hover:shadow-2xl shadow-lg">
                  <div className={`mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br ${item.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                    <item.icon className="w-8 h-8 text-white"/>
                  </div>
                  <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-6">{item.description}</p>
                </div>
              </Reveal>))}
          </div>
          </div>
        </section>

        
        <section className="relative py-24 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 via-amber-500/5 to-yellow-500/5 -z-10"/>
          <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <Reveal className="order-2 md:order-1 space-y-4">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-100 to-amber-100 text-orange-800 px-5 py-2.5 rounded-full text-sm font-semibold mb-4 border border-orange-200/50 shadow-sm">
                <Star className="w-4 h-4"/>
                <span>محیط مدرن</span>
              </div>
              <h3 className="text-2xl md:text-3xl font-extrabold">محیطی مدرن برای آرامش خاطر شما</h3>
              <p className="text-muted-foreground leading-8">
                ساختمان سه‌طبقه آزمایشگاه سلامت برای راحتی و کارایی مراجعین طراحی شده است؛ فضایی روشن، پاکیزه و کارآمد برای تجربه‌ای ممتاز.
              </p>
            </Reveal>
            <Reveal delay={200} className="order-1 md:order-2">
              <div className="relative group">
                <div className="absolute -inset-4 bg-gradient-to-r from-orange-400/25 to-amber-400/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-500"></div>
                <div className="relative overflow-hidden rounded-2xl border-2 border-orange-200/50 shadow-xl">
                  <img src="/images/about-space.jpg" alt="نمایی روشن و مدرن از فضای آزمایشگاه" loading="lazy" className="w-full h-[420px] object-cover group-hover:scale-105 transition-transform duration-500"/>
                  <div className="absolute inset-0 bg-gradient-to-t from-orange-900/20 to-transparent pointer-events-none"/>
                  <div className="absolute bottom-4 right-4 flex gap-2">
                    <div className="p-2 rounded-lg bg-white/90 backdrop-blur-sm">
                      <Heart className="w-5 h-5 text-rose-500"/>
                    </div>
                    <div className="p-2 rounded-lg bg-white/90 backdrop-blur-sm">
                      <Sparkles className="w-5 h-5 text-amber-500"/>
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
          </div>
        </section>

        
        <section className="relative container mx-auto px-4 py-24">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-primary via-primary/90 to-primary/80 p-12 text-center text-white">
              
              <div className="absolute inset-0 opacity-20">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_30px_30px,rgba(255,255,255,0.1)_2px,transparent_2px)] bg-[length:60px_60px]"/>
              </div>
              
              
              <div className="absolute top-8 left-8 w-16 h-16 bg-white/10 rounded-full animate-float"/>
              <div className="absolute bottom-8 right-8 w-20 h-20 bg-white/10 rounded-full animate-float" style={{ animationDelay: '1s' }}/>
              
              <div className="relative z-10 space-y-6">
                <h2 className="text-3xl md:text-4xl font-bold">آماده خدمت‌رسانی به شما هستیم</h2>
                <p className="text-xl text-white/95 max-w-2xl mx-auto">
                  برای دریافت مشاوره، رزرو نوبت یا هرگونه سؤال، با ما در تماس باشید
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button size="lg" variant="secondary" className="px-8 py-4 text-lg font-bold rounded-full bg-white text-primary hover:bg-white/95 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 border border-white/80" onClick={() => window.open('http://93.114.111.53:8086/general/online-ticketing', '_blank')}>
                    <span className="text-primary font-bold tracking-wide">رزرو نوبت</span>
                  </Button>
                  <Button size="lg" variant="outline" className="px-8 py-4 text-lg font-bold rounded-full border-2 border-white text-white bg-white/10 hover:bg-white/20 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
                    <span className="font-bold">تماس با ما</span>
                  </Button>
                </div>
                
                
                <div className="flex flex-col sm:flex-row items-center justify-center gap-8 pt-8 border-t border-white/20">
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-white"/>
                      <span className="text-white font-medium">شنبه تا چهارشنبه: 06:00 تا 21:00</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-white"/>
                      <span className="text-white font-medium">پنج‌شنبه: 06:00 تا 20:30</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-white"/>
                      <span className="text-white font-medium">جمعه: تعطیل</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="w-5 h-5 text-white"/>
                    <span className="text-white font-medium">۰۲۱-۴۶۸۳۳۰۱۰ | ۰۲۱-۴۶۸۳۳۰۱۱</span>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </section>
      </main>

      <Footer />
    </div>);
};
export default About;
